import {
  trackReservationEvent,
  trackPurchaseEvent,
  gaTracker,
  ecTracker,
  trackEvent,
  serializePurchase as serializePurchaseForTracking,
} from 'user-analytics';
import {
  CHECKOUT_DETAILS_CLICKED,
  COMPANY_CREATED,
  COMPANY_CREATE_OPENED,
  COMPANY_CREATION_FAILED,
  COMPANY_FOUND,
  COMPANY_NOT_FOUND,
  COMPANY_SEARCH_PERFORMED,
  COMPANY_SELECTION_CLEARED,
  CONTINUE_WITH_SEATS,
} from 'constants/TrackEvents';
import { CurrentLang } from 'utils/lang';
import { getInstance } from 'utils/taplytics';
import { removeURLParam } from 'utils/queryString/urlsCleaner';
import { isAccountHolder } from 'utils/dotersFlexibleEditing';
import { buildZoneMap, getSeatZone } from 'utils/seatPricing';
import {
  ensureRnMixpanelIdentified,
  getRnMixpanelNativeEventProps,
} from 'utils/rnMixpanelNativeProps';
import { serializeLodgingCheckboxProps } from 'utils/lodgingCheckboxAnalytics';
import { serializeCdsFrequentPassengerOptInProps } from 'utils/cdsFrequentPassengerOptInAnalytics';
import { getNextDayResult, normalizeSearchDate } from 'utils/searchAttribution';
import store from '@/store';
import errorMonitoring from '@/errorMonitoring';
import { getApplicableLegPricing } from 'utils/purchase/baseFareOverride';
import serializeReservation from './reservation';
import { getSegmentsOccupancy } from './segmentsOccupancy';
import {
  buildAutopilotPurchaseFragmentProps,
  buildAutopilotSelectedSeatsProps,
  buildAutopilotTicketProps,
  buildPriceChangeShownProps,
} from './autopilot';

function serializeEcommerceReservation(reservation) {
  if (!reservation || !Array.isArray(reservation.fragments) || !reservation.fragments.length) {
    return null;
  }

  const { transportType, departure, origin, destination, fragments, tripSlug } = reservation;
  let transporterName = [];
  let typeName;
  let hasSeatSelection;
  let commissionAmount = 0;

  if (transportType === 'bus') {
    const { line } = fragments[0];

    transporterName.push(line.name);
    typeName = 'bus';
    hasSeatSelection = line.allowsSeatSelection;
    commissionAmount = (line.commissionEstimate / 100) * reservation.pricing.total;
  } else if (transportType === 'flight') {
    transporterName.push(fragments[0].legs.map((leg) => leg.carrier.name));
    typeName = 'flight';
    hasSeatSelection = false;
    commissionAmount = (fragments[0].carrier.commissionEstimate / 100) * reservation.pricing.total;
  } else {
    typeName = 'multicarrier';
    hasSeatSelection = false;

    fragments.forEach((fragment) => {
      if (fragment.transportType === 'flight') {
        const { carrier } = fragment;

        transporterName.push(...fragment.legs.map((leg) => leg.carrier.name));
        commissionAmount += (carrier.commissionEstimate / 100) * reservation.pricing.total;
      } else {
        const { line } = fragment;

        transporterName.push(line.name);
        commissionAmount += (line.commissionEstimate / 100) * reservation.pricing.total;
      }
    });
  }

  transporterName = transporterName.join(':');

  return {
    transporterName,
    allowsSeatSelection: hasSeatSelection,
    tripSlug,
    transportType: typeName,
    total: reservation.pricing.total,
    commission: commissionAmount,
    departure,
    originId: origin.id,
    originName: origin.name,
    originCityId: origin.cityId,
    originCityName: origin.cityName,
    originCountryName: origin.countryName,
    destinationId: destination.id,
    destinationName: destination.name,
    destinationCityId: destination.cityId,
    destinationCityName: destination.cityName,
    destinationCountryName: destination.countryName,
  };
}

function serializeWalletUsed(walletType, walletBalanceUsed) {
  if (!walletType || !walletBalanceUsed) return undefined;

  return walletType;
}

/**
 * Serializes the payment details for a purchase.
 *
 * @param {string} paymentType - The type of payment used.
 * @param {string} paymentEngine - The engine handling the payment.
 * @returns {string} The display name of the payment type based on the engine.
 */
function serializePaymentType(paymentType, paymentEngine) {
  const paymentDisplays = {
    conekta: 'oxxo',
    paycash: 'paycash',
    kushki: 'kushki',
  };

  return paymentType === 'store' ? paymentDisplays[paymentEngine] : paymentType;
}

function serializeEcommercePurchase(data) {
  const {
    couponCode,
    departs,
    email,
    insuranceAmount,
    markup,
    passengerSelection,
    phone,
    phoneCode,
    returns,
    redirect = false,
    redirectedFrom = '',
    roundTrip,
    taxes,
    token,
    total,
    walletType,
    walletBalanceUsed,
    paymentType,
    paymentEngine,
    isExchange,
    passengersQuantity,
  } = data;
  return {
    email,
    departs: serializeEcommerceReservation(departs),
    discountCode: couponCode,
    insuranceAmount,
    markup,
    passengerSelection,
    phone: `${phoneCode}${phone}`,
    redirect,
    redirectedFrom,
    returns: roundTrip ? serializeEcommerceReservation(returns) : null,
    taxes,
    token,
    total,
    walletUsed: serializeWalletUsed(walletType, walletBalanceUsed),
    walletAmount: walletBalanceUsed,
    paymentType: serializePaymentType(paymentType, paymentEngine),
    isExchange,
    passengersQuantity,
  };
}

function returnSeatSelectionStep(departs) {
  const trip = departs.fragments[0];
  const departureSeatSelection = trip.transportType === 'bus' && trip.line.allowsSeatSelection;
  let step = 2;

  if (departureSeatSelection) {
    step += 1;
  }

  return step;
}

/**
 * Function to check if the purchase has adjacent seats
 * @param {Object} departs
 * @param {Object} returns
 * @returns {boolean}
 */
function checkForAdjacentSeats(departs, returns) {
  const selectedSeatsDeparts = departs?.selectedSeats || [];
  const selectedSeatsReturns = returns?.selectedSeats || [];
  const selectedSeats = selectedSeatsDeparts.concat(selectedSeatsReturns);
  return selectedSeats.some((seat) => seat.isPickedAsAdjacent);
}

/**
 * Serialize carousel session state into a flat object for purchase events.
 * Only included when the carousel was actually used.
 * @param {object} carouselSession - carouselSession slice from Redux store.
 * @returns {object} Flat carousel props, or empty object if carousel was not used.
 */
export function serializeCarouselSession(carouselSession) {
  const departure = carouselSession.departure || {};
  const ret = carouselSession.return || {};
  const anyUsed = departure.used || ret.used;

  if (!anyUsed) return {};

  return {
    departureDaysCarouselDateSelected: departure.dateSelected ?? undefined,
    departureSearchOriginalDate: departure.originalSearchDate ?? undefined,
    returnDaysCarouselDateSelected: ret.dateSelected ?? undefined,
    returnSearchOriginalDate: ret.originalSearchDate ?? undefined,
    daysCarouselInteractionCount: (departure.interactionCount || 0) + (ret.interactionCount || 0),
  };
}

/**
 * Read carousel session from the Redux store and serialize it for purchase event tracking,
 * falling back to the active search for the originally searched dates.
 *
 * `serializeCarouselSession` returns nothing unless the carousel was used, which is correct for the
 * purchase payload it also feeds but leaves every purchase without carousel interaction — the
 * majority — with no attribution at all. The carousel value wins when present because its reducer
 * keeps the first date it saw, while the search state moves as the user browses days.
 * The return date is gated on the purchase actually having a return leg: the search state keeps a
 * return date after a round-trip search that ends in a one-way purchase, and emitting it there
 * would attribute a return the user never bought.
 * The fallback is opt-in because `serializePurchase` feeds six events, and the ticket only asks
 * for `Purchase Attempt`. Without the opt-in the searched dates would start appearing on five
 * events nobody asked about — including the exchange ones, where the search in the store is the
 * replacement search, so a property with this name would carry a different meaning.
 * @param {boolean} roundTrip - Whether the purchase has a return leg.
 * @param {boolean} withSearchFallback - Whether to fall back to the active search for the dates.
 * @returns {object} Flat carousel props, carrying the searched dates when they are known.
 */
function getCarouselProps(roundTrip, withSearchFallback = false) {
  const state = store.getState();
  const carouselProps = serializeCarouselSession(state.carouselSession || {});
  const { search } = state;

  if (!withSearchFallback) return carouselProps;

  return {
    ...carouselProps,
    departureSearchOriginalDate:
      carouselProps.departureSearchOriginalDate ??
      normalizeSearchDate(search?.get('departureDate')),
    returnSearchOriginalDate: roundTrip
      ? carouselProps.returnSearchOriginalDate ?? normalizeSearchDate(search?.get('returnDate'))
      : undefined,
  };
}

/**
 * Read lodging-checkbox session from Redux and serialize for Mixpanel purchase events.
 * @returns {object} `{ lodging_checkbox_active }` when set, otherwise empty object.
 */
function getLodgingCheckboxProps() {
  const { active } = store.getState().lodgingCheckboxSession || {};
  return serializeLodgingCheckboxProps(active);
}

/**
 * Serialize the purchase data for tracking.
 * @param {Object} purchase - The purchase object.
 * @returns {Object} The serialized purchase data.
 */
function serializePurchase(purchase, { withSearchFallback = false } = {}) {
  const {
    token,
    total,
    taxes,
    markup,
    insuranceAmount,
    international,
    passengerSelection,
    departs,
    returns,
    openTicket,
    couponCode,
    redirect = false,
    redirectedFrom = '',
    recommendedTripType,
    useRecommendedTrip,
    preSelectedSeats,
    bulkTicket,
    passengers,
    roundTrip,
  } = purchase;

  // Get user and holderEditingEnabled from Redux store
  const state = store.getState();
  const { user } = state.siemprePlus?.toJS() || {};

  // Determine if first passenger is the account holder
  const isAccountHolderValue = isAccountHolder({
    purchase: { passengers },
    user,
  });

  return {
    token,
    pricing: {
      total,
      taxes,
      markup,
      insuranceAmount,
    },
    international,
    passengerSelection,
    departs: serializeReservation(departs),
    returns: serializeReservation(returns),
    openTicket,
    discountCode: couponCode,
    lang: CurrentLang({ shouldReadDefaultLanguageFromStore: false }),
    redirect,
    redirectedFrom,
    isUsingAdjacentSeats: checkForAdjacentSeats(departs, returns),
    recommendedTripType,
    useRecommendedTrip,
    resultSeats: Boolean(preSelectedSeats),
    bulkTicket: Boolean(bulkTicket),
    ...(isAccountHolderValue !== undefined && { is_account_holder: isAccountHolderValue }),
    ...getCarouselProps(roundTrip, withSearchFallback),
  };
}

/**
 * Mixpanel extra props for lodging-checkbox state. Must live in `extraProps` (5th arg
 * to trackPurchaseEvent), not in serializePurchase — user-analytics re-serializes
 * the purchase payload and strips unknown keys (same pattern as segments_occupancy).
 * @returns {object} `{ lodging_checkbox_active }` when set, otherwise empty object.
 */
function buildLodgingCheckboxExtraProps() {
  return getLodgingCheckboxProps();
}

/**
 * Mixpanel extra props for the CDS Frequent Passenger opt-in checkbox state. Must live in
 * `extraProps` (5th arg to trackPurchaseEvent), not in serializePurchase — user-analytics
 * re-serializes the purchase payload and strips unknown keys (same pattern as lodging checkbox).
 * @param {Object} purchase - The purchase object.
 * @returns {object} `{ cds_frequent_passenger_opt_in }` when set, otherwise empty object.
 */
function buildCdsFrequentPassengerOptInExtraProps(purchase) {
  return serializeCdsFrequentPassengerOptInProps(purchase);
}

/**
 * Builds the extra event props carrying per-segment bus occupancy. Returned as
 * an object so it can be spread into `extraProps` only when data is available,
 * keeping existing event payloads untouched otherwise.
 * @param {Object} purchase - The purchase object.
 * @returns {Object} Either `{ segments_occupancy }` or an empty object.
 */
function buildOccupancyExtraProps(purchase) {
  const segmentsOccupancy = getSegmentsOccupancy(purchase);
  return segmentsOccupancy ? { segments_occupancy: segmentsOccupancy } : {};
}

/**
 * Trims a raw provider service, treating anything that is not a string as absent. Guarding on
 * the type (instead of calling `?.trim?.()`) keeps a malformed `{ trim: 'invalid' }` from throwing
 * and stops an object with a custom `trim()` from smuggling a non-string into the event.
 * @param {*} service - Raw service value read from a fragment.
 * @returns {string} The trimmed service, or an empty string for non-string values.
 */
function normalizeTripService(service) {
  return typeof service === 'string' ? service.trim() : '';
}

/**
 * Builds the Mixpanel-only property for the primary outbound/return services.
 * Reads `fragments[0]` of each direction (falling back to its nested trip), then trims,
 * drops blanks and deduplicates, keeping outgoing-to-return order. Non-string provider
 * values are discarded and must never reach the event.
 * @param {Object} purchase - The purchase object.
 * @returns {Object} `{ 'Trip Services': string }` or an empty object.
 */
function buildTripServicesExtraProps(purchase) {
  const services = [purchase?.departs?.fragments?.[0], purchase?.returns?.fragments?.[0]]
    .map(
      (fragment) =>
        normalizeTripService(fragment?.service) || normalizeTripService(fragment?.trip?.service),
    )
    .filter(Boolean);

  return services.length ? { 'Trip Services': [...new Set(services)].join(',') } : {};
}

/**
 * Expected purchase ways carrying fragments, in departure-to-return order. A missing or malformed
 * expected round-trip way stays in the list so analytics cannot silently reclassify it as one-way.
 * @param {Object} purchase - The purchase object.
 * @returns {Array<{way: string, reservation: Object|undefined}>} Expected purchase ways.
 */
function getPurchaseWaysWithFragments(purchase) {
  const ways = purchase?.roundTrip ? ['departs', 'returns'] : ['departs'];
  return ways.map((way) => ({ way, reservation: purchase?.[way] }));
}

/**
 * Applicable pricing blocks for one expected purchase way. Invalid fragment collections retain an
 * explicit placeholder so shared AutoPilot context survives while cross-leg summaries are omitted.
 * @param {{way: string, reservation: Object|undefined}} purchaseWay - Expected purchase way.
 * @param {boolean} isRoundTrip - Whether the purchase expects both legs.
 * @returns {Array<Object|undefined>} Applicable pricing candidates for the way.
 */
function getApplicableWayPricings({ way, reservation }, isRoundTrip) {
  if (!reservation || typeof reservation !== 'object' || Array.isArray(reservation)) {
    return [undefined];
  }
  if (!Array.isArray(reservation.fragments) || reservation.fragments.length === 0) {
    return [undefined];
  }

  return reservation.fragments.map((fragment) => {
    return getApplicableLegPricing(fragment, { way, isRoundTrip });
  });
}

/**
 * Expected API-returned purchase ways, in departure-to-return order. Keeping a malformed expected
 * round-trip way in the list lets the builder omit untrustworthy cross-leg summaries instead of
 * silently reclassifying the purchase as one-way.
 * @param {Object} purchase - The purchase object.
 * @returns {Array<Object|undefined>} Expected purchase ways.
 */
function getPurchaseWaysWithReturnedTickets(purchase) {
  const ways = purchase?.roundTrip ? ['departs', 'returns'] : ['departs'];
  return ways.map((way) => purchase?.[way]);
}

/**
 * Authoritative tickets for one normalized purchase way. Parsed purchase responses derive the
 * aggregate way-level collection from fragments, and the reducer explicitly replaces fragments
 * after locking while its deep merge can retain stale way-level rows. Therefore current fragments
 * win whenever that normalized location exists; way-level tickets are the fallback for shapes
 * without fragments.
 * Malformed fragment ticket collections leave an explicit invalid member for builder omission.
 * @param {Object} reservation - One returned purchase way.
 * @returns {Array<*>} Returned ticket candidates for the way.
 */
function getAuthoritativeWayTickets(reservation) {
  if (!reservation || typeof reservation !== 'object' || Array.isArray(reservation)) {
    return [undefined];
  }

  const hasFragmentLocation = Object.prototype.hasOwnProperty.call(reservation, 'fragments');
  if (hasFragmentLocation && !Array.isArray(reservation.fragments)) return [undefined];
  if (hasFragmentLocation) {
    return reservation.fragments.flatMap((fragment) => {
      return Array.isArray(fragment?.tickets) ? fragment.tickets : [undefined];
    });
  }
  return Array.isArray(reservation.tickets) ? reservation.tickets : [undefined];
}

/**
 * AutoPilot extra props for `Purchase Attempt`, from the charged applicable pricing of the
 * currently returned purchase fragments, retained as explicit departure/return groups.
 * Bounded and non-fatal: a failure inside the enrichment leaves the unchanged base payload.
 * @param {Object} purchase - The purchase object.
 * @returns {Object} Mixpanel-only AutoPilot props, or an empty object.
 */
export function buildAutopilotPurchaseAttemptExtraProps(purchase) {
  try {
    const isRoundTrip = Boolean(purchase?.roundTrip);
    const legPricingGroups = getPurchaseWaysWithFragments(purchase).map((purchaseWay) => {
      return getApplicableWayPricings(purchaseWay, isRoundTrip);
    });
    return buildAutopilotPurchaseFragmentProps({ legPricingGroups });
  } catch (error) {
    errorMonitoring.notify({ error, info: { context: 'purchaseAttemptTracker.autopilot' } });
    return {};
  }
}

/**
 * AutoPilot extra props for the post-lock events (`Passengers Created`, `Confirm Itinerary`),
 * from the API-returned tickets of the current purchase, retained as explicit leg groups.
 * Bounded and non-fatal: a failure inside the enrichment leaves the unchanged base payload.
 * @param {Object} purchase - The purchase object.
 * @param {string} context - Diagnostic context label for the non-fatal report.
 * @returns {Object} Mixpanel-only AutoPilot props, or an empty object.
 */
function buildAutopilotTicketExtraProps(purchase, context) {
  try {
    const legTicketGroups = getPurchaseWaysWithReturnedTickets(purchase).map(
      getAuthoritativeWayTickets,
    );
    return buildAutopilotTicketProps({ legTicketGroups });
  } catch (error) {
    errorMonitoring.notify({ error, info: { context } });
    return {};
  }
}

/**
 * Whether either leg of the purchase is an open ticket.
 * @param {Object} purchase - The purchase object.
 * @returns {boolean} Whether the purchase involves an open ticket.
 */
function isOpenTicketPurchase(purchase) {
  const legs = [purchase?.departs, purchase?.returns];
  return legs.some(
    (leg) => leg?.openTicket || leg?.fragments?.some?.((fragment) => fragment?.openTicket),
  );
}

/**
 * Builds the Mixpanel-only property reporting whether the chosen outbound departs on a day other
 * than the one originally searched. Mixpanel-only, so it must not reach `sharedExtraProps`.
 * @param {Object} purchase - The purchase object.
 * @returns {Object} `{ 'Next Day Result': boolean }`, or an empty object when it cannot be computed.
 */
function buildNextDayResultExtraProps(purchase) {
  // An open ticket's departure is a nominal filler with no relation to what the user searched —
  // the analytics package nulls `Departure` and `Arrival` for them for exactly this reason
  // (`serializers/trip.js:176-177`). Comparing against it would emit a believable boolean built
  // from a date that means nothing, so the property is omitted instead.
  if (isOpenTicketPurchase(purchase)) return {};

  const nextDayResult = getNextDayResult(
    purchase?.departs?.departure,
    getCarouselProps(false, true).departureSearchOriginalDate,
  );

  return nextDayResult === undefined ? {} : { 'Next Day Result': nextDayResult };
}

/**
 * Tracks the purchase attempt event.
 * @param {Object} purchase - The purchase object.
 */
export function purchaseAttemptTracker(purchase) {
  ensureRnMixpanelIdentified();

  const { env } = store.getState().whitelabelConfig;
  const serializedPurchase = serializePurchase(purchase, { withSearchFallback: true });
  const rnNativeEventProps = getRnMixpanelNativeEventProps();
  const occupancyProps = buildOccupancyExtraProps(purchase);
  // Shared by every destination below, Taplytics included — Mixpanel-only props must not land here.
  const sharedExtraProps = {
    ...rnNativeEventProps,
    ...occupancyProps,
    ...buildLodgingCheckboxExtraProps(),
    ...buildCdsFrequentPassengerOptInExtraProps(purchase),
  };
  if (env.taplytics.enabled) {
    const taplytics = getInstance();
    taplytics.track('Purchase Attempt', { ...serializedPurchase, ...sharedExtraProps });
  }
  const queryParams = new URLSearchParams(window?.location.search);
  if (queryParams.has('redirected_to')) {
    // Re-serialized without the search fallback on purpose: `Email Link Clicked` is a different
    // event, and the ticket only asks for the searched dates on `Purchase Attempt`. Reusing
    // `serializedPurchase` here would leak them into it, since the package serializer maps those
    // keys to their Mixpanel names for whatever payload it is handed.
    const analyticsData = {
      ...serializePurchaseForTracking(serializePurchase(purchase)),
      'Email Type': queryParams.get('redirected_to'),
    };
    queryParams.delete('redirected_to');
    window?.history.replaceState({}, null, removeURLParam('redirected_to', window?.location.href));
    trackEvent('Email Link Clicked', analyticsData);
  }
  trackPurchaseEvent('Purchase Attempt', serializedPurchase, undefined, undefined, {
    ...sharedExtraProps,
    ...buildTripServicesExtraProps(purchase),
    ...buildAutopilotPurchaseAttemptExtraProps(purchase),
    ...buildNextDayResultExtraProps(purchase),
    ...buildAutopilotPurchaseAttemptExtraProps(purchase),
  });
  ecTracker.trackAddToCart(serializeEcommercePurchase(purchase));
}

export function exchangeAttemptTracker(purchase) {
  trackPurchaseEvent('Exchange Attempt', serializePurchase(purchase));
  ecTracker.trackAddToCart(serializeEcommercePurchase(purchase));
}

/**
 * Track the "Passengers Created" funnel event.
 *
 * @param {Object} purchase - Serialized purchase payload (includes `selectedFrequentPassengers` when passed from Passengers).
 */
export function passengersTracker(purchase) {
  const { selectedFrequentPassengers, departs, returns } = purchase;

  const selectedSeatsDeparts = departs?.selectedSeats || [];
  const selectedSeatsReturns = returns?.selectedSeats || [];
  const allSelectedSeats = selectedSeatsDeparts.concat(selectedSeatsReturns);

  const seats = allSelectedSeats.map((seat) => {
    const seatLevel =
      seat.seatLevel != null && !Number.isNaN(Number(seat.seatLevel))
        ? Number(seat.seatLevel)
        : undefined;

    return {
      number: seat.number ?? undefined,
      floor: seat.seatFloor ?? seatLevel,
    };
  });

  const extraProps = {
    'Selected Frequent Passengers': selectedFrequentPassengers.map((passenger) => ({
      firstName: passenger.firstName,
      lastName: passenger.lastName,
      email: passenger.email,
    })),
    'Selected Frequent Passengers Count': selectedFrequentPassengers.length,
    'Seats': seats,
    ...buildOccupancyExtraProps(purchase),
    ...buildLodgingCheckboxExtraProps(),
    ...buildCdsFrequentPassengerOptInExtraProps(purchase),
    ...buildAutopilotTicketExtraProps(purchase, 'passengersTracker.autopilot'),
  };
  trackPurchaseEvent(
    'Passengers Created',
    serializePurchase(purchase),
    undefined,
    undefined,
    extraProps,
  );
  ecTracker.trackPurchaseFunnel(1, serializeEcommercePurchase(purchase));
}

/**
 * Track the "Exchange Passengers Created" event.
 *
 * @param {Object} purchase - Purchase payload for exchange flow.
 */
export function exchangePassengersTracker(purchase) {
  trackPurchaseEvent(
    'Exchange Passengers Created',
    serializePurchase(purchase),
    undefined,
    undefined,
    undefined,
  );
  ecTracker.trackPurchaseFunnel(1, serializeEcommercePurchase(purchase));
}

export function seatChangeTracker(purchase = {}, way, fromView) {
  if (fromView === 'results') {
    trackEvent('Seat Change', {
      Way: way,
      View: fromView,
    });
    return;
  }

  const serializedPurchase = serializeEcommercePurchase(purchase);
  const { departs, returns } = purchase;

  if (way === 'departure') {
    // segment
    trackReservationEvent('Seat Change', serializeReservation(departs, 'departs'));
    // GA
    if (departs.transportType === 'bus' && departs.fragments[0].line.allowsSeatSelection) {
      ecTracker.trackPurchaseFunnel(2, serializedPurchase);
    }
  } else {
    // segment
    trackReservationEvent('Seat Change', serializeReservation(returns, 'returns'));
    // GA
    if (returns.transportType === 'bus' && returns.fragments[0].line.allowsSeatSelection) {
      ecTracker.trackPurchaseFunnel(returnSeatSelectionStep(departs), serializedPurchase);
    }
  }
}

export function exchangeSeatChangeTracker(purchase, way, fromView) {
  if (fromView === 'results') {
    trackEvent('Exchange Seat Change', {
      Way: way,
      View: fromView,
    });
    return;
  }

  const { departs, returns } = purchase;
  const serializedPurchase = serializeEcommercePurchase(purchase);

  if (way === 'departure' && departs) {
    // segment
    trackReservationEvent('Exchange Seat Change', serializeReservation(departs, 'departs'));
    // GA - only if we have valid ecommerce data
    if (
      serializedPurchase &&
      departs.transportType === 'bus' &&
      departs.fragments?.[0]?.line?.allowsSeatSelection
    ) {
      ecTracker.trackPurchaseFunnel(2, serializedPurchase);
    }
  } else if (way === 'return' && returns) {
    // segment
    trackReservationEvent('Exchange Seat Change', serializeReservation(returns, 'returns'));
    // GA - only if we have valid ecommerce data
    if (
      serializedPurchase &&
      returns.transportType === 'bus' &&
      returns.fragments?.[0]?.line?.allowsSeatSelection
    ) {
      ecTracker.trackPurchaseFunnel(returnSeatSelectionStep(departs), serializedPurchase);
    }
  }
}

export function confirmItineraryTracker(purchase) {
  trackPurchaseEvent('Confirm Itinerary', serializePurchase(purchase), undefined, undefined, {
    ...buildOccupancyExtraProps(purchase),
    ...buildLodgingCheckboxExtraProps(),
    ...buildCdsFrequentPassengerOptInExtraProps(purchase),
    ...buildAutopilotTicketExtraProps(purchase, 'confirmItineraryTracker.autopilot'),
  });
}

/**
 * Tracks the `BookingReview Details Clicked` event, fired when the user opens
 * the trip details modal from the booking review. Enriched with
 * `segments_occupancy` so the open interaction carries the same bus capacity
 * signal as `Purchase Attempt`, `Passengers Created` and `Confirm Itinerary`.
 *
 * @param {Object} purchase - The purchase object from the store.
 */
export function bookingReviewDetailsClickedTracker(purchase) {
  trackEvent(CHECKOUT_DETAILS_CLICKED, buildOccupancyExtraProps(purchase));
}

export function exchangeConfirmItineraryTracker(purchase) {
  trackPurchaseEvent('Exchange Confirm Itinerary', serializePurchase(purchase));
}

export function purchaseOnHoldTracker(token, callback) {
  gaTracker.trackPurchaseOnHold(token, callback);
}

export function purchaseTracker(
  { purchase, funnelStep, cardBrand, paymentType, paymentEngine, isExchange, passengersQuantity },
  callback,
) {
  const serializedPurchase = serializeEcommercePurchase({
    ...purchase,
    paymentType,
    paymentEngine,
    isExchange,
    passengersQuantity,
  });

  ecTracker.trackPurchase(serializedPurchase);
  ecTracker.trackPurchaseFunnel(funnelStep, serializedPurchase, cardBrand, callback);
}

/**
 * Event to track when the user click on the return CTA offer reminder
 * The return CTA is present in passengers and checkout pages if ft flag is enabled
 */
export function returnCTATracker(buttonVariant) {
  trackEvent('Return Reminder Clicked', {
    Button: buttonVariant,
  });
}

/**
 * Tracks actions in the price change modal.
 *
 * For the accepted `Shown` observation the caller passes the exact totals the rendered modal
 * displays plus the currently returned purchase; the payload gains numeric `New Total` and
 * `Former Total`, boolean `Price Lower`, and `AutoPilot Applied: true` only for a canonical
 * returned purchase. The enrichment is bounded and non-fatal, and the existing `Continue` /
 * `View Results` button calls keep their prior payload.
 * @param {string} action - Action performed in the modal:
 * - "Shown": The modal transitioned into a rendered, visible presentation
 * - "View Results": Click to return to schedules
 * - "Continue": Click to continue with purchase
 * @param {Object} [shownContext] - Optional `Shown` observation context.
 * @param {*} [shownContext.newTotal] - New total the modal displays.
 * @param {*} [shownContext.formerTotal] - Former total the modal displays.
 * @param {Object} [shownContext.purchase] - Currently returned purchase state.
 */
export function priceChangeModalTracker(action, shownContext) {
  let shownProps = {};
  if (shownContext) {
    try {
      shownProps = buildPriceChangeShownProps(shownContext);
    } catch (error) {
      shownProps = {};
      errorMonitoring.notify({ error, info: { context: 'priceChangeModalTracker.autopilot' } });
    }
  }
  trackEvent('Price Change Modal', {
    Action: action,
    ...shownProps,
  });
}

/**
 * Tracks when user selects the quick purchase option.
 * @param {string} from - Origin of the selection: 'schedule' or 'seats'.
 * @param {boolean} isRecommended - Whether the trip is a recommended trip.
 */
export function quickPurchaseSelectedTracker(from, isRecommended) {
  trackEvent('Quick purchase selected', {
    'From': from,
    'Is Recommended': isRecommended,
  });
}

/**
 * Tracks when user clicks back to search from QuickCheckout.
 */
export function quickPurchaseBackToSearchTracker() {
  trackEvent('Quick purchase back to search');
}

/**
 * Tracks when user clicks edit passengers from QuickCheckout.
 */
export function quickPurchasePassengerEditTracker() {
  trackEvent('Quick purchase passenger edit');
}

/**
 * Tracks when user clicks edit seats from QuickCheckout.
 */
export function quickPurchaseSeatsEditTracker() {
  trackEvent('Quick purchase seats edit');
}

/**
 * Tracks when user clicks edit buyer data from QuickCheckout.
 */
export function quickPurchaseBuyerEditTracker() {
  trackEvent('Quick purchase buyer edit');
}

/**
 * Tracks when user clicks the main purchase button from QuickCheckout.
 */
export function quickPurchaseMainButtonTracker() {
  trackEvent('Quick purchase main button');
}

/** Tracks a billing-company lookup without emitting document PII. */
export function companySearchPerformedTracker() {
  trackEvent(COMPANY_SEARCH_PERFORMED);
}

/** Tracks a successful billing-company lookup without emitting company PII. */
export function companyFoundTracker() {
  trackEvent(COMPANY_FOUND);
}

/** Tracks a billing-company lookup miss without emitting company PII. */
export function companyNotFoundTracker() {
  trackEvent(COMPANY_NOT_FOUND);
}

/** Tracks when manual billing-company capture is shown. */
export function companyCreateOpenedTracker() {
  trackEvent(COMPANY_CREATE_OPENED);
}

/** Tracks successful billing-company creation without emitting company PII. */
export function companyCreatedTracker() {
  trackEvent(COMPANY_CREATED);
}

/** Tracks failed billing-company creation without emitting company PII. */
export function companyCreationFailedTracker() {
  trackEvent(COMPANY_CREATION_FAILED);
}

/** Tracks when the user clears a selected billing company. */
export function companySelectionClearedTracker() {
  trackEvent(COMPANY_SELECTION_CLEARED);
}

/**
 * Tracks seat-selection confirmation and includes selected zone metadata when available.
 *
 * `meetsMinimum` carries the exact boolean the caller is about to enforce as its minimum-seat
 * rule, so the event distinguishes accepted from rejected continuations; the tracker fires
 * before rejection either way. `pricing` optionally carries the displayed leg pricing block so
 * canonical AutoPilot journeys add their selected-seat summary — the enrichment is bounded and
 * non-fatal, and callers without the new context keep their prior payload.
 * @param {object} params - way, selectedSeats, seatPriceZones, meetsMinimum, pricing.
 * @param {string} params.way - Trip way ('departure' or 'return').
 * @param {Array<object>} params.selectedSeats - Currently selected seats.
 * @param {Array<object>} params.seatPriceZones - Resolved seat price zones for current trip.
 * @param {boolean} [params.meetsMinimum] - Exact result of the caller's minimum-seat predicate.
 * @param {object} [params.pricing] - Displayed leg pricing block for AutoPilot context.
 */
export function continueWithSeatsTracker(params) {
  const { way, selectedSeats = [], seatPriceZones = [], meetsMinimum } = params;
  const selectedPassengerSeats = selectedSeats.filter((seat) => !seat.isPickedAsAdjacent);
  const hasZones = Array.isArray(seatPriceZones) && seatPriceZones.length > 0;

  const meetsMinimumProps =
    typeof meetsMinimum === 'boolean' ? { 'Seat Selection Meets Minimum': meetsMinimum } : {};

  // Bounded, non-fatal AutoPilot enrichment over the non-companion passenger seats.
  let autopilotProps = {};
  try {
    const { pricing } = params;
    autopilotProps = buildAutopilotSelectedSeatsProps({
      pricing,
      selectedSeats: selectedPassengerSeats,
    });
  } catch (error) {
    autopilotProps = {};
    errorMonitoring.notify({ error, info: { context: 'continueWithSeatsTracker.autopilot' } });
  }

  if (!hasZones || selectedPassengerSeats.length === 0) {
    trackEvent(CONTINUE_WITH_SEATS, { way, ...meetsMinimumProps, ...autopilotProps });
    return;
  }

  const zoneMap = buildZoneMap({ seatPriceZones });
  const zoneLabelsSet = new Set();

  selectedPassengerSeats.forEach((seat) => {
    const { label } = getSeatZone(seat, zoneMap);
    if (label) zoneLabelsSet.add(label);
  });

  trackEvent(CONTINUE_WITH_SEATS, {
    way,
    seatSelection: {
      zones: Array.from(zoneLabelsSet),
      zonesCount: zoneLabelsSet.size,
    },
    ...meetsMinimumProps,
    ...autopilotProps,
  });
}

/**
 * Tracks when a quick purchase is being created (attempt).
 */
export function quickPurchaseAttemptTracker() {
  trackEvent('Quick purchase attempt');
}

/**
 * Tracks the Quick Purchase Status event.
 * Fires once per search results view after the GrowthBook eligibility attribute is applied.
 * @param {object} params - Event parameters.
 * @param {boolean} params.eligible - Whether the user matches Quick Purchase eligibility criteria (independent of experiment).
 * @param {boolean} params.shown - Whether Quick Purchase was shown to the user (experiment variant is on).
 * @param {string} [params.reason] - Reason Quick Purchase was not shown (only when shown=false).
 * @param {number} params.passengersCount - Number of available passengers.
 * @param {string} params.passengersSource - Source: 'frequent' | 'local_storage' | 'none'.
 */
export function quickPurchaseStatusTracker({
  eligible,
  shown,
  reason,
  passengersCount,
  passengersSource,
}) {
  const properties = {
    'Eligible': eligible,
    'Shown': shown,
    'Passengers Count': passengersCount,
    'Passengers Source': passengersSource,
  };
  if (!shown && reason) {
    properties.Reason = reason;
  }
  trackEvent('Quick Purchase Status', properties);
}

/**
 * Tracks when the user arrives at the quick-checkout screen.
 */
export function quickPurchaseViewTracker() {
  trackEvent('Quick purchase view');
}

/**
 * Tracks when Quick Purchase opens with incomplete buyer/passenger data, i.e. one or more
 * tenant-required fields are missing/invalid and the user must complete them before paying
 * (CON-1076). The missing fields are keyed as `passenger_<n>.<field>` / `buyer.<field>`.
 * @param {string[]} missingFields - Detected missing or invalid required fields.
 */
export function quickPurchaseIncompleteDataTracker(missingFields = []) {
  trackEvent('Incomplete data for Quick Purchase detected', {
    'Missing Fields': missingFields,
    'Missing Fields Count': missingFields.length,
  });
}

/**
 * Tracks every Quick Purchase payment attempt with the buyer/passenger data the purchase holds.
 *
 * Fires on complete attempts too: the incomplete ones are only meaningful as a share of all
 * attempts. Unlike `quickPurchaseIncompleteDataTracker`, this carries the purchase token — trackEvent
 * attaches no super properties, so without it an event cannot be traced back to a purchase.
 *
 * Field lists use the same `buyer.<field>` / `passenger_<n>.<field>` identifiers as the
 * incomplete-data event, so both can be analysed together.
 * @param {object} params - Tracking parameters.
 * @param {string} params.token - Purchase token.
 * @param {string[]} params.missingFields - Required fields absent from the purchase.
 * @param {string[]} params.unsavedBuyerFields - Buyer fields filled in the form but absent from the purchase.
 * @param {object} params.buyer - Buyer data held by the purchase.
 * @param {object} params.buyerForm - Buyer data held by the form.
 * @param {Array<object>} params.passengers - Passenger data held by the purchase.
 * @param {string} params.paymentOption - Selected payment option.
 */
export function quickPurchasePaymentAttemptTracker({
  token,
  missingFields = [],
  unsavedBuyerFields = [],
  buyer = {},
  buyerForm = {},
  passengers = [],
  paymentOption,
}) {
  trackEvent('Quick Purchase Payment Attempt', {
    'Token': token,
    'Payment Option': paymentOption,
    'Missing Fields': missingFields,
    'Missing Fields Count': missingFields.length,
    'Unsaved Buyer Fields': unsavedBuyerFields,
    'Has Incomplete Data': missingFields.length > 0 || unsavedBuyerFields.length > 0,
    'Buyer': buyer,
    'Buyer Form': buyerForm,
    'Passengers': passengers,
    'Passengers Count': passengers.length,
  });
}

/**
 * Tracks click on the Tapatio Tour banner CTA from purchase complete.
 * @param {object} params - Tracking parameters.
 * @param {string} [params.token] - Purchase token.
 * @param {string} [params.brand] - Whitelabel brand.
 * @param {string} [params.url] - Full banner URL opened on click (carries the brand ref).
 * @param {string} [params.origin] - Trip origin city name.
 * @param {string} [params.destination] - Trip destination city name.
 * @param {string} [params.context] - UI context where click happened.
 */
export function tapatioBannerClickTracker({
  token,
  brand,
  url,
  origin,
  destination,
  context = 'purchase_complete',
}) {
  trackEvent('Tapatio Tour Banner Clicked', {
    'Purchase Token': token,
    'Brand': brand,
    'Banner URL': url,
    'Origin': origin,
    'Destination': destination,
    'Context': context,
  });
}
